// Feature: worklist-upload-visibility — Task 6.1
// Pure view-selection helper for the `/worklists` route (Req 4.1, 4.2;
// design §"Req 4 — frontend: detail route").
//
// The `/worklists` base route serves both the Worklist_List_View and the
// Worklist_Detail_View; which one renders is decided solely by the presence
// of a non-empty `worklistId` query parameter. Extracted as a pure function
// so the selection logic is property-testable (design Property 4) without
// mounting the router.

/** The views the `/worklists` route can resolve to. */
export type WorklistView = 'item' | 'detail' | 'list';

/**
 * A minimal, URLSearchParams-like reader. Accepts a real `URLSearchParams`
 * (its `get` returns `string | null`) so callers can pass the value from
 * `useSearchParams()` directly.
 */
export interface WorklistViewParams {
  get(name: string): string | null;
}

/**
 * Select the worklists view from the route's query parameters.
 *
 *   - `'item'`   — a non-empty `worklistId` AND a non-empty `itemId` are both
 *     present (the Work_Item_Detail_View, worklist-item-detail Req 2, 4).
 *   - `'detail'` — a non-empty `worklistId` only (the Worklist_Detail_View).
 *   - `'list'`   — otherwise. An absent/empty `worklistId` degrades to the list
 *     view rather than an error (missing/empty worklistId).
 */
export function selectWorklistView(params: WorklistViewParams): WorklistView {
  const worklistId = params.get('worklistId');
  if (!worklistId) return 'list';
  const itemId = params.get('itemId');
  return itemId ? 'item' : 'detail';
}

/**
 * Relative path to the Work_Item_Detail_View for one item
 * (worklist-item-detail Req 4). Both params are URL-encoded so keys containing
 * reserved characters (e.g. `#`, `&`, `/`) resolve correctly.
 */
export function workItemDetailPath(worklistId: string, itemId: string): string {
  const wl = encodeURIComponent(worklistId);
  const item = encodeURIComponent(itemId);
  return `/worklists?worklistId=${wl}&itemId=${item}`;
}

/**
 * Absolute, shareable deep link to the Work_Item_Detail_View for one item
 * (worklist-item-detail Req 3, 4). `origin` is typically
 * `window.location.origin`; a trailing slash is trimmed so the result is a
 * clean absolute URL.
 */
export function buildWorkItemDeepLink(
  origin: string,
  worklistId: string,
  itemId: string,
): string {
  const base = typeof origin === 'string' ? origin.replace(/\/+$/, '') : '';
  return `${base}${workItemDetailPath(worklistId, itemId)}`;
}
